import { useRef } from 'react'
import { EMAIL_CLAIMED_OAUTH_CODE } from '@papan/types'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from '@papan/i18n'
import { useAuth } from '../hooks/useAuth'
import { AuthLayout } from '../components/AuthLayout'
import { SignInForm, type SignInFormHandle } from '../components/SignInForm'
import { safeNext } from '../lib/safe-next'

export function LoginPage() {
  const { refreshSession } = useAuth()
  const [searchParams] = useSearchParams()
  /**
   * A failed social sign-in comes back as `?error=<code>`, and this maps the
   * codes we know to copy. It does NOT render the parameter.
   *
   * The first version did: it un-underscored whatever was in the URL and showed
   * it in a styled red alert above the credential form. That is a phishing
   * amplifier — `/login?error=Your_account_is_suspended._Email_billing@x_to_
   * reactivate.` renders an authoritative-looking sentence over HTTPS on the
   * genuine login page. React escapes it so there is no XSS, but the text is the
   * attack. It also leaked better-auth's internal tokens as prose for ordinary
   * failures ("unable to get user info", "account not linked").
   *
   * Anything unrecognised gets the generic line: the user still learns the
   * sign-in failed, and an attacker controls nothing but which of our sentences
   * appears.
   */
  /**
   * A Map, not an object literal, and a `typeof` check — because the object
   * version turned this page into a crash.
   *
   * `OAUTH_ERRORS[key]` reaches `Object.prototype` for `__proto__`,
   * `constructor`, `toString` and friends, and `??` does not catch them: they
   * are objects and functions, not nullish. `/login?error=__proto__` therefore
   * put a non-string into the JSX and React threw "Objects are not valid as a
   * React child", which the app's ErrorBoundary turned into "Something went
   * wrong" — no email field, no password field, no Google button, and a Reload
   * that reloads the same URL and crashes again. `?error=constructor` rendered
   * an empty red alert.
   *
   * That is a crafted `/login?...` link taking the real sign-in page down, which
   * is the same class of harm the allowlist was added to prevent, arriving by a
   * different door. The code this replaced could not do it: it always produced a
   * string.
   */
  const OAUTH_ERRORS = new Map<string, string>([
    // Keyed off the shared constant, never a copy of the wording: changing the
    // server's sentence used to leave this keyed to the old one, silently
    // downgrading the specific message to the generic fallback.
    //
    // PROMISES NOTHING, for the same reason the server string does not. This
    // copy renders ONLY on the OAuth surface — `/login?error=` is reachable no
    // other way — where the user may have signed up with Google and have no
    // password at all, and `/request-password-reset` looks up `users.email`
    // only, so for a secondary it sends nothing. An earlier version said "Try
    // signing in with your password", which is the dead end this branch keeps
    // finding.
    [EMAIL_CLAIMED_OAUTH_CODE,
      'That email address already belongs to an account.'],
    ['account_not_linked',
      'That account is not linked to this sign-in method. Sign in the way you did before.'],
    // The apostrophe is better-auth's, verbatim: `redirectOnError("email_doesn't_match")`
    // percent-encodes to `email_doesn%27t_match` and decodes back with the
    // apostrophe intact. Spelling it `email_doesn_t_match` here meant the key
    // could never match and the specific copy was dead — a second hand-typed
    // copy of a string that lives in someone else's source, which is the exact
    // thing `EMAIL_CLAIMED_OAUTH_CODE` was derived to avoid.
    ["email_doesn't_match",
      'The address from that provider does not match this account.'],
  ])
  const rawOauthError = searchParams.get('error')
  const mapped = rawOauthError === null ? undefined : OAUTH_ERRORS.get(rawOauthError)
  const oauthError = rawOauthError === null
    ? ''
    : (typeof mapped === 'string' ? mapped : 'We could not sign you in with that provider. Try again, or use your email and password.')
  const nextPath = safeNext(searchParams.get('next'))
  const { t } = useTranslation('auth')
  const formRef = useRef<SignInFormHandle>(null)

  return (
    <AuthLayout
      onDevPasswordClick={() => formRef.current?.setPassword('PapanDev2026!')}
      footer={
        <span>
          {t('login.noAccount')}{' '}
          {/* Where the person was headed goes with them: sign-up carries
              `next` through verification (a picker's sign-in detour). */}
          <Link
            to={searchParams.get('next') ? `/signup?next=${encodeURIComponent(nextPath)}` : '/signup'}
            tabIndex={5}
            className="text-blue-400 hover:text-blue-300"
          >
            {t('login.signUp')}
          </Link>
        </span>
      }
    >
      {oauthError && (
        <div role="alert" className="mb-4 rounded border border-red-800 bg-red-900/20 p-3 text-sm text-red-400">
          {oauthError}
        </div>
      )}
      <SignInForm
        ref={formRef}
        googleCallbackPath={nextPath}
        onSuccess={async () => {
          await refreshSession()
          window.location.href = nextPath
        }}
      />
    </AuthLayout>
  )
}
